import type {
  GeoImportWorksFrontendMethods,
  MlNotificationsHubFrontendMethods,
  ObjectTreeFrontendMethods,
  PickerProgressFrontendMethods,
  ProjectsHubFrontendMethods,
  SeismicFrontendMethods,
} from '@core/core/api'
import { convertToProjectElementInfo, convertToTabElementInfo } from '@core/core/api'
import {
  tryAddUploadableFiles,
  tryRemoveUploadableFiles,
  tryUpdateUploadFileState,
  updateMultipartLoadingState,
  updateStateOnCubeAddedToProject,
} from '@core/core/store'
import type { ActiveEntity, RestoredElement } from '@core/core/types'
import {
  addMlStatusNotification,
  addPickerProgressNotification,
  addPublishedElementNotification,
} from '@notifications/actions'
import { convertToArray } from 'common/helpers'
import { messengerRef } from 'common/messenger'
import type { ProjectElementInfo } from 'common/types'

import { notifyNotElementsNotDeleted } from '../../channelEvents'
import type { Hubs } from '../../signalR'
import {
  addNewNodes,
  deleteNodes,
  getAddedElementsSelectability,
  updateExistingNodes,
} from '../../treeObjects'
import { updateTreeElementStates } from '../../workArea'
import { checkStatus } from './asserts'
import {
  getElementAddedForSpaActions,
  getNotifyStatusActions,
  getProgressActions,
} from './dataToActions'

type NotifyActionsMap = {
  ObjectTreeHub: ObjectTreeFrontendMethods
  PickerProgressHub: PickerProgressFrontendMethods
  SeismicImportProgressHub: SeismicFrontendMethods
  GeoImportWorksHub: GeoImportWorksFrontendMethods
  MlHub: MlNotificationsHubFrontendMethods
  ProjectsHub: ProjectsHubFrontendMethods
}

const notifyActionsMap: NotifyActionsMap = {
  ObjectTreeHub: {
    ElementAdded: data => {
      const nodes = data.map(convertToProjectElementInfo)

      const entities: ActiveEntity[] = getEntitiesFromNodes(nodes)
      messengerRef.current?.send({ type: 'entitiesUpdated', payload: entities })

      return [
        addNewNodes(nodes),
        ...data.map(element =>
          updateTreeElementStates(element.tabElements.map(convertToTabElementInfo)),
        ),
        getAddedElementsSelectability(nodes),
      ]
    },
    ElementAddedForSpa: data => {
      const nodes = data.map(convertToProjectElementInfo)
      return getElementAddedForSpaActions(nodes)
    },
    ElementUpdated: data => {
      const apiProjectElements = convertToArray(data)
      const nodes = apiProjectElements.map(convertToProjectElementInfo)
      return [
        updateExistingNodes(nodes),
        ...apiProjectElements.map(element =>
          updateTreeElementStates(element.tabElements.map(convertToTabElementInfo)),
        ),
      ]
    },
    ElementStates: data => {
      const apiTabElements = convertToArray(data)
      const elements = apiTabElements.map(convertToTabElementInfo)
      return [updateTreeElementStates(elements)]
    },
    ElementDeletedToTrash: data => {
      const nodes = data.map(convertToProjectElementInfo)

      const entities: ActiveEntity[] = getEntitiesFromNodes(nodes)
      messengerRef.current?.send({ type: 'entitiesUpdated', payload: entities })
      return [
        deleteNodes(nodes),
        ...data.map(element =>
          updateTreeElementStates(element.tabElements.map(convertToTabElementInfo)),
        ),
      ]
    },
    ElementPublished: data => {
      const apiProjectElements = convertToArray(data)
      const nodes = apiProjectElements.map(convertToProjectElementInfo)

      messengerRef.current?.send({ type: 'elementPublished', payload: undefined })

      const filteredData = nodes.filter(i => i.elementType !== 'horizonsFolder')
      const notifications = filteredData.map(addPublishedElementNotification)
      return [
        ...notifications,
        updateExistingNodes(nodes),
        ...apiProjectElements.map(elem =>
          updateTreeElementStates(elem.tabElements.map(convertToTabElementInfo)),
        ),
      ]
    },
    ElementSwitchDomain: data => {
      const apiProjectElements = convertToArray(data)
      const nodes = apiProjectElements.map(convertToProjectElementInfo)
      return [
        updateExistingNodes(nodes),
        ...apiProjectElements.map(elem =>
          updateTreeElementStates(elem.tabElements.map(convertToTabElementInfo)),
        ),
      ]
    },
    NotElementDeleted: data => {
      const elements = data.map(convertToProjectElementInfo)
      notifyNotElementsNotDeleted(elements)
    },
    ElementRestored: data => {
      const nodes = data.map(convertToProjectElementInfo)
      const elements: RestoredElement[] = nodes.map(node => {
        return {
          elementType: node.elementType,
        }
      })
      messengerRef.current?.send({ type: 'undoDelete', payload: elements })

      return [
        addNewNodes(nodes),
        ...data.map(elem => updateTreeElementStates(elem.tabElements.map(convertToTabElementInfo))),
        getAddedElementsSelectability(nodes),
      ]
    },
  },
  PickerProgressHub: {
    PickerProgress: data => {
      const status = data.status
      checkStatus(status)
      addPickerProgressNotification({ ...data, status })
    },
    AddCubeToProject: data => {
      return [updateStateOnCubeAddedToProject(data)]
    },
  },
  SeismicImportProgressHub: {
    UploadProgress: data => {
      return [updateMultipartLoadingState(data)]
    },
    Progress: data => getProgressActions(data),
    UploadedFileByProject: data => {
      return [tryAddUploadableFiles([data])]
    },
    DeletedFileByProject: data => {
      return [tryRemoveUploadableFiles([data])]
    },
    NotifyImportStatus: data => {
      return [tryUpdateUploadFileState(data)]
    },
  },
  GeoImportWorksHub: {
    NotifyStatus: data => getNotifyStatusActions(data),
    NotifyProgress: data => {
      return [
        updateMultipartLoadingState({
          taskId: data.uid,
          progress: data.percent,
          partNumber: data.complete,
        }),
      ]
    },
  },
  MlHub: {
    NotifyStatus: addMlStatusNotification,
  },
  ProjectsHub: { Notify: console.log },
}

const getEntitiesFromNodes = (nodes: ProjectElementInfo[]): ActiveEntity[] =>
  nodes.map(node => ({
    entityName: node.name,
    entityType: node.elementType,
    entityId: node.projectEntityUid,
  }))

const isKeyInObject = <T extends object>(key: string | symbol | number, obj: T): key is keyof T =>
  key in obj

export const getNotifyMethods = (hub: Hubs) => Object.keys(notifyActionsMap[hub])

export const getActionForNotify = (
  hub: Hubs,
  notifyName: string,
): ((...args: unknown[]) => unknown) | undefined =>
  isKeyInObject(notifyName, notifyActionsMap[hub]) ? notifyActionsMap[hub][notifyName] : undefined
